<script lang="ts" setup>
import { computed } from 'vue'
import { v4 as uuid } from 'uuid'
import TagsSelectorFlipnovel from './TagsSelector.flipnovel.vue'
import { brand } from '@/config/brands'

const props = defineProps<{
  config: any
  loading: boolean
  tabLoading: boolean
  activeTab: number
  tagsList: any[]
  contentTabs: string[]
  activeContentTab: number
  filteredBooks: any[]
  booksData: any[]
  ending: boolean
  fetching: boolean
  infiniteScrollProps: any
  gotoBook: (id: string, firstChapterId?: string) => void
  setContentContainer: (el: HTMLElement | null) => void
}>()

const emit = defineEmits<{
  (event: 'update:active-tab', value: number): void
  (event: 'update:active-content-tab', value: number): void
}>()

const wrapClass = computed(() => {
  const base = 'relative h-full flex flex-col rounded-12px rr-tags-wrap'
  return brand === 'storlyn'
    ? `${base} storlyn-tags-wrap storlyn-card-shadow`
    : `${base} bg-n-bg-card`
})
</script>

<template>
  <div v-if="loading" class="py-120px flex items-center justify-center text-13px text-n-text-3">
    <div class="animate-spin">
      <i-icon-park-outline-loading />
    </div>
  </div>
  <div v-else id="tags" class="h-100dvh bg-n-bg-global overflow-scroll relative">
    <BasicNavbar :scale="true" class="color-n-text-2" />

    <TagsSelectorFlipnovel
      :active-tab="activeTab"
      :tags-list="tagsList"
      @update:active-tab="(newVal) => emit('update:active-tab', newVal)"
    />

    <div id="tagsContainer" class="h-[calc(100dvh-120px)] px-4 mb-20px mt-0">
      <div :class="wrapClass">
        <div class="top-0 z-9 w-full my-4 px-2">
          <div class="relative h-8 bg-n-bg-tag rounded-18px">
            <div class="absolute inset-0 flex">
              <button
                v-for="(tab, index) in contentTabs"
                :key="index"
                class="filter-tab flex-1 h-full text-sm font-medium rounded-[18px]"
                :class="[
                  activeContentTab === index
                    ? 'bg-n-primary text-white'
                    : 'text-n-text-2',
                ]"
                @click="emit('update:active-content-tab', index)"
              >
                {{ tab }}
              </button>
            </div>
          </div>
        </div>

        <div :ref="setContentContainer" v-bind="infiniteScrollProps" class="flex-1 overflow-y-auto">
          <div v-if="tabLoading" class="py-120px flex items-center justify-center text-13px text-n-text-3">
            <div class="animate-spin">
              <i-icon-park-outline-loading />
            </div>
          </div>

          <template v-else>
            <div class="space-y-3 px-3 pb-4">
              <div v-for="(item, index) in filteredBooks" :key="index" class="rr-book-item h-[110px]" @click="gotoBook(item.id, item.firstChapterId)">
                <BizBookCover
                  :id="uuid()"
                  :src="item.contentCoverUrl || ''"
                  :extra="{
                    author: item?.authorInfo?.author,
                    module_name: 'tags',
                    ...item,
                  }"
                  class="w-[83px] rounded-10px"
                />

                <div class="flex-1 min-w-0 h-full flex flex-col justify-between">
                  <p class="text-15px font-bold line-clamp-1 text-n-text-1">
                    {{ item.contentName }}
                  </p>
                  <p class="text-13px mt-8px line-clamp-2 text-n-text-2">
                    {{ item.introduce }}
                  </p>

                  <div class="mt-8px flex items-center">
                    <div class="flex-1 min-w-0 mr-2">
                      <span class="rr-tag-text bg-n-bg-tag rounded-14px text-11px px-6px py-3px text-n-text-2">
                        {{ item.contentClassName }}
                      </span>
                    </div>
                    <div class="flex items-center justify-end text-10px text-n-text-3 flex-shrink-0">
                      <i-icon-park-outline-preview-open class="w-3 h-3 mr-1 flex-shrink-0" />
                      <span class="whitespace-nowrap">{{ item.viewUsers || 0 }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="ending && !fetching" class="py-15px text-13px text-n-text-3 text-center">
              {{ config.texts.noMore }}
            </div>
            <div v-if="fetching && booksData.length > 0" class="py-15px flex items-center justify-center text-13px text-n-text-3">
              <div class="animate-spin">
                <i-icon-park-outline-loading />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.rr-tags-wrap {
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.storlyn-tags-wrap {
  background: #FFFFFF;
  border: 0;
}

.rr-book-item {
  display: flex;
  align-items: stretch;
  gap: 12px;
}

.rr-tag-text {
  display: inline-block;
  vertical-align: middle;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
